import './TagCreationModal.scss';
import { useCallback, useState } from 'react';
import { Modal, ModalProps } from 'react-bootstrap';
import { BulkTagConfigStep } from './BulkTagConfigStep';
import { CreativeAllocationPicker } from './CreativeAllocationPicker';
import { MetadataForm } from './MetadataForm';
import { TagCreationForm } from './TagCreationForm';
import { StagedTagData, TagData } from './utils';

type TagCreationModalPageType =
  'initialForm' | 'bulkConfig' | 'creativeAllocation' | 'metadataForm';

const modalSizeMap: Record<string, ModalProps['size']> = {
  initialForm: 'lg',
  bulkConfig: 'xl',
  creativeAllocation: 'xl',
  metadataForm: 'lg'
};

interface TagCreationModalProps {
  initialTag: TagData;
  setInitialTag: (tag: TagData) => void;
  show: boolean;
  startingPage: TagCreationModalPageType;
  onClose: () => void;
  stageTags: (tags: StagedTagData[]) => void;
  isEditMode: boolean;
  flashtalkingCampaigns?: { campaign_id: number; campaign: string; is_managed_service: boolean }[];
  additionalSourceTags?: TagData[];
}

export const TagCreationModal = ({
  initialTag,
  setInitialTag,
  show,
  startingPage,
  onClose,
  stageTags,
  isEditMode,
  flashtalkingCampaigns = [],
  additionalSourceTags = []
}: TagCreationModalProps) => {
  const defaultPage: TagCreationModalPageType =
    startingPage === 'initialForm' && additionalSourceTags.length > 0 ? 'bulkConfig' : startingPage;
  const [page, setPage] = useState<TagCreationModalPageType>(defaultPage);
  const [tagsToStage, setTagsToStage] = useState<StagedTagData[]>([]);
  // True when the current step was reached by going Back. Revisited allocation /
  // metadata steps are treated like edit mode so they reload the prior input and
  // don't immediately auto-advance.
  const [isRevisiting, setIsRevisiting] = useState(false);

  // The step each page returns to. The starting page has no predecessor.
  const previousPageMap: Partial<Record<TagCreationModalPageType, TagCreationModalPageType>> = {
    metadataForm: 'creativeAllocation',
    creativeAllocation: defaultPage
  };
  const backPage = page === defaultPage ? undefined : previousPageMap[page];
  const goBack = backPage
    ? () => {
        setIsRevisiting(true);
        setPage(backPage);
      }
    : undefined;
  const goForward = useCallback((next: TagCreationModalPageType) => {
    setIsRevisiting(false);
    setPage(next);
  }, []);

  const makeTagName = useCallback(
    (
      {
        device,
        length,
        additionalParam,
        dimensions
      }: {
        device?: string | null;
        length?: string | null;
        additionalParam?: string | null;
        dimensions?: string | null;
      },
      sourceTag: TagData = initialTag
    ) => {
      const { company, derivedID, network, platform, description } = sourceTag;
      const parts = [`${derivedID} ${company} ${network} ${platform}`];
      if (device) {
        parts.push(`Device: ${device}`);
      }
      const endParts: string[] = [];
      if (description) {
        endParts.push(description);
      }
      if (additionalParam) {
        endParts.push(additionalParam);
      }
      if (length) {
        endParts.push(`${length}s`);
      }
      if (dimensions) {
        endParts.push(dimensions);
      }
      if (endParts.length) {
        parts.push(endParts.join(' '));
      }
      return parts.join(' | ');
    },
    [initialTag]
  );

  const breakDownTagBySelections = useCallback(
    (sourceTag: TagData = initialTag): StagedTagData[] => {
      const tagsFromSelections: StagedTagData[] = [];

      let devices: (string | null)[] = [null];
      let lengths: (string | null)[] = [null];
      const additionalParams = sourceTag.additionalParams || [];
      let dimensionsList: (string | null)[] = [null];

      if (sourceTag.deviceMap) {
        devices = Object.keys(sourceTag.deviceMap).filter(d => sourceTag.deviceMap![d]);
      }
      if (sourceTag.lengthMap) {
        lengths = Object.keys(sourceTag.lengthMap).filter(l => sourceTag.lengthMap![l]);
      }
      if (sourceTag.dimensionsMap) {
        dimensionsList = Object.keys(sourceTag.dimensionsMap).filter(
          d => sourceTag.dimensionsMap![d]
        );
      }

      const formSelections = {
        deviceMap: sourceTag.deviceMap,
        lengthMap: sourceTag.lengthMap,
        additionalParams: sourceTag.additionalParams,
        vendor: sourceTag.vendor,
        campaignID: sourceTag.campaignID,
        startDate: sourceTag.startDate,
        endDate: sourceTag.endDate,
        placementType: sourceTag.placementType,
        transcodeVendor: sourceTag.transcodeVendor,
        extraTrackingPixels: sourceTag.extraTrackingPixels,
        dimensions: sourceTag.dimensions,
        dimensionsMap: sourceTag.dimensionsMap
      };

      for (const device of devices) {
        for (const length of lengths) {
          for (const dimensions of dimensionsList) {
            if (additionalParams.length) {
              for (const additionalParam of additionalParams) {
                tagsFromSelections.push({
                  ...sourceTag,
                  ...formSelections,
                  device: device ?? undefined,
                  length: length ?? undefined,
                  dimensions: dimensions ?? undefined,
                  additionalParam,
                  name: makeTagName({ device, length, additionalParam, dimensions }, sourceTag)
                });
              }
            } else {
              tagsFromSelections.push({
                ...sourceTag,
                ...formSelections,
                device: device ?? undefined,
                length: length ?? undefined,
                dimensions: dimensions ?? undefined,
                name: makeTagName({ device, length, dimensions }, sourceTag)
              });
            }
          }
        }
      }
      return tagsFromSelections;
    },
    [initialTag, makeTagName]
  );

  const submitInitialForm = useCallback(
    (updatedTag: TagData) => {
      const allTags = [
        ...breakDownTagBySelections(updatedTag),
        ...additionalSourceTags.flatMap(sourceTag => breakDownTagBySelections(sourceTag))
      ];
      setTagsToStage(allTags);
      goForward('creativeAllocation');
    },
    [additionalSourceTags, breakDownTagBySelections, goForward]
  );

  const submitBulkConfigForm = useCallback(
    (updatedTags: TagData[]) => {
      const allStagedTags = updatedTags.flatMap(tag => breakDownTagBySelections(tag));
      setTagsToStage(allStagedTags);
      goForward('creativeAllocation');
    },
    [breakDownTagBySelections, goForward]
  );

  const submitCreativeAllocation = useCallback(() => goForward('metadataForm'), [goForward]);

  const submitMetadataForm = useCallback(() => {
    stageTags(tagsToStage);
    onClose();
  }, [onClose, stageTags, tagsToStage]);

  return (
    <Modal
      size={modalSizeMap[page]}
      show={show}
      onHide={onClose}
      onExited={() => {
        setPage(defaultPage);
        setIsRevisiting(false);
      }}
      dialogClassName='tag-creation-modal'
      centered
    >
      {page === 'bulkConfig' && (
        <BulkTagConfigStep
          allTags={[initialTag, ...additionalSourceTags]}
          onSubmit={submitBulkConfigForm}
          flashtalkingCampaigns={flashtalkingCampaigns}
        />
      )}
      {page === 'initialForm' && (
        <TagCreationForm
          tag={initialTag}
          setTag={setInitialTag}
          onSubmit={submitInitialForm}
          flashtalkingCampaigns={flashtalkingCampaigns}
          additionalSourceTagCount={additionalSourceTags.length}
        />
      )}
      {page === 'creativeAllocation' && (
        <CreativeAllocationPicker
          tags={tagsToStage}
          setTags={setTagsToStage}
          onSubmit={submitCreativeAllocation}
          onBack={goBack}
          isEditMode={isEditMode || isRevisiting}
        />
      )}
      {page === 'metadataForm' && (
        <MetadataForm
          tags={tagsToStage}
          setTags={setTagsToStage}
          onSubmit={submitMetadataForm}
          onBack={goBack}
          company={initialTag.company}
          isEditMode={isEditMode || isRevisiting}
        />
      )}
    </Modal>
  );
};
